/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import {
  BundleTypeKind,
  IBundle,
  ICondition,
  IDiagnosticReport,
  IMedicationRequest,
  IMedicationStatement,
  IObservation,
  IProcedure,
  IReference,
} from '@ahryman40k/ts-fhir-types/lib/R4';

/** Rather than fetching _all_ the data, only get a subset using elements.
 *
 *  Note the _elements and IDiagnosticReportSimplified  MUST match, so typescript
 *  can catch any errors we make.
 */
export const diagnosticReport_elements = [
  'effectiveDateTime',
  'id',
  'code',
  'result',
  'resourceType',
  'presentedForm',
  'conclusion',
  'conclusionCode',
  'search',
] as const;
export const diagnosticReport_elements_str = diagnosticReport_elements.join(',');
export type IDiagnosticReportSimplified = Pick<
  IDiagnosticReport,
  'effectiveDateTime' | 'id' | 'code' | 'resourceType' | 'presentedForm' | 'conclusion' | 'conclusionCode'
> & { result?: IReference[]; isCareEverywhere?: true }; // This should actually be either a IReference or a IObservationSimplified but I can't get typescript to do that
// const diagnosticReportBundle_elements = ['type', 'entry'] as const;
export type IDiagnosticReportBundleSimplified = Pick<
  IBundle & {
    type: BundleTypeKind._searchset;
    resourceType: 'Bundle';
  },
  'type' | 'entry' | 'resourceType'
>;

export type IMedicationRequestBundle = IBundle & {
  type: BundleTypeKind._searchset;
  resourceType: 'Bundle';
  entry: Array<{ resource: IMedicationRequest }>;
};

export type PatientMedication = IMedicationRequest | IMedicationStatement;

export type IPatientMedicationBundle = IBundle & {
  type: BundleTypeKind._searchset;
  resourceType: 'Bundle';
  entry: Array<{ resource: PatientMedication }>;
};

export type IObservationBundle = IBundle & {
  type: BundleTypeKind._searchset;
  resourceType: 'Bundle';
  entry: Array<{ resource: IObservation }>;
};

export type IConditionBundle = IBundle & {
  type: BundleTypeKind._searchset;
  resourceType: 'Bundle';
  entry: Array<{ resource: ICondition }>;
};

export type IProcedureBundle = IBundle & {
  type: BundleTypeKind._searchset;
  resourceType: 'Bundle';
  entry: Array<{ resource: IProcedure }>;
};

/*export const observation_elements = [
  'id',
  'code',
  'valueQuantity',
  'effectiveDateTime',
  'category',
  'referenceRange',
  'valueCodeableConcept',
  'valueRatio',
  'valueString',
  'valueBoolean',
  'valueDateTime',
  'dataAbsentReason',
  'interpretation',
  'note',
  'issued',
] as const;
export const observation_elements_str = observation_elements.join(',');
export type IObservationSimplified = Pick<
  IObservation,
  | 'id'
  | 'code'
  | 'valueQuantity'
  | 'effectiveDateTime'
  | 'category'
  | 'referenceRange'
  | 'valueCodeableConcept'
  | 'valueRatio'
  | 'valueString'
  | 'valueBoolean'
  | 'valueDateTime'
  | 'dataAbsentReason'
  | 'interpretation'
  | 'note'
  | 'issued'
>;*/
// Disable the above for now
export type IObservationSimplified = IObservation;

/** IObservationSimplified from IObservation fhir, but we create a single pearlCode from the more complicated code array that they give us.
 *  This code is used for ordering the observations and hiding/showing them.
 *  Note that this might not be unique, even inside a panel.
 */
export type IObservationWithPearlCode = IObservationSimplified & {
  pearlCode: string;
  whitelistId: string;
  observationGroupId: string;
};

export enum ObservationValueInterpretationEnum {
  NORMAL = 'Normal',
  ABNORMAL = 'Abnormal',
  LOW = 'BelowNormalRange',
  HIGH = 'AboveNormalRange',
  NONE = 'NoRangeSpecified',
}
